import { useState, useEffect } from 'react';
import { Users, Mail, Phone, Car, Loader2, Clock, CheckCircle2, Receipt, ShieldCheck, ArrowRightLeft } from 'lucide-react';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ReassignLesseeDialog } from '../components/leasing/ReassignLessee';
import { AssignmentLogCard } from '../components/leasing/AssignmentLogCard';

const eur = (v, el) => `€${(v || 0).toLocaleString(el ? 'el-GR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso, el) => (iso ? new Date(iso).toLocaleString(el ? 'el-GR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '-');

const VehicleRow = ({ v, el }) => (
    <tr className="border-t border-slate-100" data-testid={`leasing-client-vehicle-${v.id}`}>
        <td className="py-2 pr-3 font-medium text-slate-800">{v.license_plate}</td>
        <td className="py-2 pr-3 text-slate-600">{v.make} {v.model} {v.year}</td>
        <td className="py-2 pr-3">
            {v.status === 'pending' ? (
                <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded"><Clock className="w-3 h-3" />{el ? 'Σε αναμονή' : 'Pending'}</span>
            ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded"><CheckCircle2 className="w-3 h-3" />{el ? 'Επιβεβαιωμένο' : 'Confirmed'}</span>
            )}
        </td>
        <td className="py-2 pr-3 text-slate-700">{(v.current_mileage || 0).toLocaleString(el ? 'el-GR' : 'en-GB', { maximumFractionDigits: 1 })} km</td>
        <td className="py-2 pr-3 text-slate-700">{v.checkins} / {v.checkouts}</td>
        <td className="py-2 pr-3 text-slate-500 text-xs">{fmtDate(v.last_checkin, el)}</td>
        <td className="py-2 text-slate-500 text-xs">{fmtDate(v.last_checkout, el)}</td>
        <td className="py-2 pl-3 text-slate-600 text-xs">{v.km_limit ? `${v.km_limit.toLocaleString(el ? 'el-GR' : 'en-GB')} km` : '-'}</td>
        <td className="py-2 pl-3 text-slate-600 text-xs">{v.contract_end_date ? new Date(v.contract_end_date).toLocaleDateString(el ? 'el-GR' : 'en-GB') : '-'}</td>
        <td className="py-2 pl-3 text-slate-800 text-xs font-medium" data-testid={`leasing-client-vehicle-charge-${v.id}`}>{v.status === 'pending' ? '-' : eur(v.charge_this_month, el)}</td>
    </tr>
);

const ClientCard = ({ client, el, isAdmin, onMove }) => (
    <div className="fleet-card p-5 space-y-4" data-testid={`leasing-client-${client.id}`}>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
                <h2 className="text-base md:text-lg font-semibold text-slate-800">{client.name}</h2>
                <p className="text-xs uppercase tracking-wide text-slate-400">{client.edition}</p>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
                    <span className="flex items-center gap-1"><Users className="w-4 h-4" />{client.admin_name || '-'}</span>
                    <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{client.admin_email || '-'}</span>
                    <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{client.admin_phone || '-'}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2 text-sm" data-testid={`leasing-client-managers-${client.id}`}>
                    <ShieldCheck className="w-4 h-4 text-slate-400" />
                    {client.managers?.length
                        ? client.managers.map((m) => <span key={m.id} className="text-xs bg-[#1B7EB5]/10 text-[#1B7EB5] px-2 py-0.5 rounded">{m.name}</span>)
                        : <span className="text-xs text-amber-600">{el ? 'Χωρίς υπεύθυνο στόλου' : 'No fleet manager'}</span>}
                    {isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => onMove(client)} className="h-7 text-[#1B7EB5]" data-testid={`leasing-client-move-${client.id}`}>
                            <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />{client.managers?.length ? (el ? 'Μεταφορά' : 'Move') : (el ? 'Ανάθεση' : 'Assign')}
                        </Button>
                    )}
                </div>
            </div>
            <div className="flex gap-6 text-right">
                <div><p className="text-xl font-bold text-[#1B7EB5]" data-testid={`leasing-client-vehicles-count-${client.id}`}>{client.vehicles.length}</p><p className="text-xs text-slate-500">{el ? 'Οχήματα' : 'Vehicles'}</p></div>
                <div><p className="text-xl font-bold text-[#1B7EB5]">{client.total_km.toLocaleString(el ? 'el-GR' : 'en-GB')}</p><p className="text-xs text-slate-500">{el ? 'Σύνολο km' : 'Total km'}</p></div>
                <div><p className="text-xl font-bold text-[#1B7EB5]" data-testid={`leasing-client-billable-${client.id}`}>{client.billable_vehicles}</p><p className="text-xs text-slate-500">{el ? 'Χρεώσιμα' : 'Billable'}</p></div>
                <div><p className="text-xl font-bold text-slate-800" data-testid={`leasing-client-charge-${client.id}`}>{eur(client.charge_this_month, el)}</p><p className="text-xs text-slate-500">{el ? 'Χρέωση μήνα έως σήμερα' : 'Charged so far'}</p></div>
                <div><p className="text-xl font-bold text-slate-800" data-testid={`leasing-client-estimate-${client.id}`}>{eur(client.monthly_estimate, el)}</p><p className="text-xs text-slate-500">{el ? `Εκτίμηση μήνα (${eur(client.price_per_vehicle, el)}/όχημα)` : `Monthly est. (${eur(client.price_per_vehicle, el)}/vehicle)`}</p></div>
            </div>
        </div>
        {client.vehicles.length ? (
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-slate-500">
                            <th className="pb-2 pr-3">{el ? 'Πινακίδα' : 'Plate'}</th>
                            <th className="pb-2 pr-3">{el ? 'Όχημα' : 'Vehicle'}</th>
                            <th className="pb-2 pr-3">{el ? 'Κατάσταση' : 'Status'}</th>
                            <th className="pb-2 pr-3">{el ? 'Χιλιόμετρα' : 'Odometer'}</th>
                            <th className="pb-2 pr-3">Check-in / Check-out</th>
                            <th className="pb-2 pr-3">{el ? 'Τελευταίο Check-in' : 'Last check-in'}</th>
                            <th className="pb-2">{el ? 'Τελευταίο Check-out' : 'Last check-out'}</th>
                            <th className="pb-2 pl-3">{el ? 'Όριο km' : 'Km limit'}</th>
                            <th className="pb-2 pl-3">{el ? 'Λήξη σύμβασης' : 'Contract end'}</th>
                            <th className="pb-2 pl-3">{el ? 'Χρέωση μήνα' : 'Month charge'}</th>
                        </tr>
                    </thead>
                    <tbody>{client.vehicles.map((v) => <VehicleRow key={v.id} v={v} el={el} />)}</tbody>
                </table>
            </div>
        ) : (
            <p className="text-sm text-slate-400 flex items-center gap-2"><Car className="w-4 h-4" />{el ? 'Δεν έχουν ανατεθεί οχήματα' : 'No vehicles assigned'}</p>
        )}
    </div>
);

const BillingSummary = ({ clients, el }) => {
    const sum = (k) => clients.reduce((acc, c) => acc + (c[k] || 0), 0);
    return (
        <div className="fleet-card p-5 flex flex-wrap items-center gap-8" data-testid="leasing-clients-billing-summary">
            <div className="flex items-center gap-2"><Receipt className="w-5 h-5 text-[#1B7EB5]" /><span className="font-semibold text-slate-800">{el ? 'Χρέωση Cofleet ανά μισθωτή' : 'Cofleet charges per lessee'}</span></div>
            <div><p className="text-xl font-bold text-[#1B7EB5]">{sum('billable_vehicles')}</p><p className="text-xs text-slate-500">{el ? 'Χρεώσιμα οχήματα' : 'Billable vehicles'}</p></div>
            <div><p className="text-xl font-bold text-slate-800" data-testid="leasing-clients-total-charge">{eur(sum('charge_this_month'), el)}</p><p className="text-xs text-slate-500">{el ? 'Μήνας έως σήμερα' : 'Month to date'}</p></div>
            <div><p className="text-xl font-bold text-slate-800" data-testid="leasing-clients-total-estimate">{eur(sum('monthly_estimate'), el)}</p><p className="text-xs text-slate-500">{el ? 'Εκτίμηση μήνα' : 'Monthly estimate'}</p></div>
        </div>
    );
};

export default function LeasingClientsPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const { user } = useAuth();
    const [clients, setClients] = useState(null);
    const [moving, setMoving] = useState(null);

    const load = () => api.get('/leasing/clients').then((res) => setClients(res.data)).catch(() => setClients([]));

    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-6" data-testid="leasing-clients-page">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">{el ? 'Μισθωτές' : 'Lessees'}</h1>
                <p className="text-zinc-500">{el ? 'Οι πελάτες που έχουν συνδεθεί με την εταιρεία σας' : 'Clients linked to your leasing company'}</p>
            </div>
            {clients?.length > 0 && <BillingSummary clients={clients} el={el} />}
            {clients === null ? (
                <Loader2 className="w-6 h-6 animate-spin text-[#1B7EB5]" />
            ) : clients.length ? (
                clients.map((c) => <ClientCard key={c.id} client={c} el={el} isAdmin={user?.role === 'admin'} onMove={setMoving} />)
            ) : (
                <div className="fleet-card p-10 text-center text-slate-500" data-testid="leasing-clients-empty">
                    {el ? 'Δεν υπάρχουν ακόμα συνδεδεμένοι μισθωτές. Οι πελάτες σας σας επιλέγουν κατά την εγγραφή ή από τις Ρυθμίσεις.'
                        : 'No linked lessees yet. Clients select you at sign-up or from Settings.'}
                </div>
            )}
            {user?.role === 'fleet_manager' && <AssignmentLogCard el={el} />}
            <ReassignLesseeDialog open={Boolean(moving)} onOpenChange={(o) => !o && setMoving(null)} client={moving} el={el} onDone={load} />
        </div>
    );
}
